import { Signal } from '@verlo/shared/components/ui/signal';
import { Notice } from '@verlo/shared/components/ui/notice';
import { useState } from 'react';
import { Button } from '@verlo/shared/components/ui/button';
import { Badge } from '@verlo/shared/components/ui/badge';
import { Checkbox } from '@verlo/shared/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@verlo/shared/components/ui/dialog';
import { Plus, ShoppingCart } from 'lucide-react';
import { OptionChips } from '@verlo/shared/components/ui/segmented';

const ItemDetailDialog = ({
  open,
  onOpenChange,
  item,
  onAddToOrder,
  formatCurrency,
}) => {
  const [selectedModifiers, setSelectedModifiers] = useState([]);
  const [selectedSize, setSelectedSize] = useState(null);

  const handleClose = () => {
    setSelectedModifiers([]);
    setSelectedSize(null);
    onOpenChange(false);
  };

  const toggleModifier = (modName) => {
    setSelectedModifiers(prev =>
      prev.includes(modName) ? prev.filter(m => m !== modName) : [...prev, modName]
    );
  };

  const handleAddToOrder = () => {
    if (item) {
      onAddToOrder(item, { modifiers: selectedModifiers, size: selectedSize });
    }
    handleClose();
  };

  // Off right now (a Sunday roast on a Tuesday): the sheet still shows what's in it, for the
  // guest who asks, but it can't go on an order. This button was the way round the card's
  // own refusal, and the server then refused the round with the roast on it.
  const off = item?.available_now === false;
  const hasModifiers = item?.modifiers?.length > 0;
  const hasSizes = item?.has_sizes && item?.size_options?.length > 1;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent size="form">
        <DialogHeader>
          <DialogTitle className="text-xl">{item?.name}</DialogTitle>
          <DialogDescription>{item?.description}</DialogDescription>
        </DialogHeader>
        
        {item && (
          <div className="space-y-4">
            {/* Price and dietary info */}
            <div className="flex items-center justify-between">
              <span className="text-2xl font-bold text-verlo">
                {selectedSize
                  ? formatCurrency(item.size_options?.find(s => s.name === selectedSize)?.price || item.price)
                  : formatCurrency(item.price)
                }
              </span>
              <div className="flex gap-2">
                {item.is_vegetarian && (
                  <Badge className="bg-signal-ok-tint text-signal-ok">Vegetarian</Badge>
                )}
                {item.is_vegan && (
                  <Badge className="bg-signal-ok-tint text-signal-ok">Vegan</Badge>
                )}
                {item.is_gluten_free && (
                  <Badge className="bg-signal-warn-tint text-signal-warn">Gluten-Free</Badge>
                )}
              </div>
            </div>

            {/* Size Selection */}
            {hasSizes && (
              <div className="space-y-2">
                <h4 className="font-semibold text-sm text-ink-soft uppercase tracking-wide">Size</h4>
                <OptionChips
                  value={selectedSize}
                  onChange={setSelectedSize}
                  options={item.size_options.map((size) => ({ value: size.name, label: `${size.name} · ${formatCurrency(size.price)}` }))}
                />
              </div>
            )}

            {/* Modifiers */}
            {hasModifiers && (
              <div className="space-y-2">
                <h4 className="font-semibold text-sm text-ink-soft uppercase tracking-wide">
                  Modifiers
                </h4>
                <div className="space-y-1">
                  {item.modifiers.map((mod, idx) => {
                    const modName = typeof mod === 'string' ? mod : mod.name;
                    const modPrice = typeof mod === 'string' ? 0 : (mod.price || 0);
                    return (
                      <div
                        key={idx}
                        className={`flex items-center justify-between p-2.5 border cursor-pointer transition-colors ${
                          selectedModifiers.includes(modName)
                            ? 'bg-verlo/5 border-verlo'
                            : 'hover:bg-surface-2 border-hairline'
                        }`}
                        onClick={() => toggleModifier(modName)}
                      >
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={selectedModifiers.includes(modName)}
                            className="rounded-none pointer-events-none"
                          />
                          <span className="text-sm">{modName}</span>
                        </div>
                        {modPrice > 0 && (
                          <span className="text-sm text-ink-soft">+{formatCurrency(modPrice)}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Ingredients */}
            {item.ingredients?.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-semibold text-sm text-ink-soft uppercase tracking-wide">
                  Ingredients
                </h4>
                <div className="bg-surface-2 rounded-lg p-3 space-y-2">
                  {item.ingredients.map((ing, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <span>{ing.name || ing}</span>
                      {ing.contains?.length > 0 && (
                        <div className="flex gap-1">
                          {ing.contains.map((allergen, i) => (
                            <Badge key={i} variant="outline" className="text-xs text-signal-stop border-signal-stop-line bg-signal-stop-tint">
                              {allergen}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Allergens Warning */}
            {item.allergens?.length > 0 && (
              <Notice tone="stop" word="Contains">
                <div className="flex flex-wrap gap-1">
                  {item.allergens.map((allergen, idx) => (
                    <Signal key={idx} tone="stop" dot={false}>{allergen}</Signal>
                  ))}
                </div>
              </Notice>
            )}
            
            {/* No ingredients/allergens message */}
            {(!item.ingredients || item.ingredients.length === 0) && 
             (!item.allergens || item.allergens.length === 0) && (
              <div className="text-sm text-ink-soft text-center py-4">
                No ingredient or allergen information available for this item
              </div>
            )}
          </div>
        )}
        
        {off && (
          <Notice tone="warn" word="Not on now" data-testid="detail-off-notice">
            {item.hours ? `${item.name} is on ${item.hours}.` : `${item.name} isn't on right now.`}
          </Notice>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose}>
            Close
          </Button>
          {!onAddToOrder || off ? null : (
          <Button 
            className="bg-verlo hover:bg-verlo-hover"
            onClick={handleAddToOrder}
            data-testid="detail-add-to-order-btn"
          >
            <ShoppingCart size={16} className="mr-2" />
            Add to Order
            {selectedModifiers.length > 0 && (
              <Badge className="ml-2 bg-white/20 text-white text-[10px]">
                +{selectedModifiers.length}
              </Badge>
            )}
          </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ItemDetailDialog;
